import { Button, Container, showAlert } from '@/components';
import { useFiscalYearContext } from '@/context/FiscalYearContext/FiscalYearContextProvider';
import React, { useEffect, useState } from 'react';
import { FaEdit } from 'react-icons/fa';
import { IoCloseSharp } from 'react-icons/io5';

const FiscalYear = () => {
  const [editMode, setEditMode] = useState(null);
  const [inputValues, setInputValues] = useState({});
  const [errors, setErrors] = useState({});
  const [newFiscalYear, setNewFiscalYear] = useState('');
  const [inputError, setInputError] = useState('');
  const [loading, setLoading] = useState(true); // Loading state
  const {
    fetchFiscalYears,
    fiscalYearsData,
    fiscalYear,
    setFiscalYear,
    initialState,
    modifyFiscalYear,
    createFiscalYear,
  } = useFiscalYearContext();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true); // Start loading
      await fetchFiscalYears();
      setLoading(false); // Stop loading
    };
    fetchData();
  }, []);

  const toggleEditMode = (id) => {
    if (editMode === id) {
      setErrors({});
      setFiscalYear(initialState);
      setEditMode(null);
      setInputValues({});
    } else {
      const selectedFiscalYear = fiscalYearsData.find((fy) => fy.id === id);
      if (selectedFiscalYear) {
        setFiscalYear({ ...selectedFiscalYear });
        setEditMode(id);

        const initialInputValues = selectedFiscalYear.totalWorkingDays.reduce(
          (acc, month) => {
            acc[month.id] = month.monthlyWorkingDays;
            return acc;
          },
          {},
        );
        setInputValues(initialInputValues);
      }
    }
  };

  const handleInputChange = (monthId, value) => {
    if (value === '') {
      // Clear error when the value is cleared
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[monthId]; // Remove error when input is cleared
        return newErrors;
      });

      setInputValues((prev) => ({
        ...prev,
        [monthId]: value,
      }));
    } else {
      const intValue = parseInt(value, 10);

      if (isNaN(intValue) || intValue < 1 || intValue > 30) {
        setErrors((prev) => ({
          ...prev,
          [monthId]: `Value must be between 1 and 30`,
        }));
      } else {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[monthId]; // Remove error on valid input
          return newErrors;
        });
        setInputValues((prev) => ({
          ...prev,
          [monthId]: intValue,
        }));
      }
    }
  };

  const handleBlur = (monthId, value, monthName) => {
    const intValue = parseInt(value, 10);
    const maxDays = monthName === 'FEBRUARY' ? 28 : 30;

    if (intValue < 1 || intValue > maxDays) {
      setErrors((prev) => ({
        ...prev,
        [monthId]: `Value must be between 1 and ${maxDays}`,
      }));
    } else {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[monthId]; // Remove error if value is valid
        return newErrors;
      });
    }
  };

  const handleSave = async () => {
    let hasErrors = false;

    fiscalYear.totalWorkingDays.forEach((month) => {
      const monthValue = inputValues[month.id];
      const maxDays = month.month === 'FEBRUARY' ? 28 : 30;

      if (!monthValue || monthValue < 1 || monthValue > maxDays) {
        hasErrors = true;
        setErrors((prev) => ({
          ...prev,
          [month.id]: `Value must be between 1 and ${maxDays}`,
        }));
      }
    });

    if (hasErrors) {
      showAlert('error', 'Please fix the errors before saving.');
      return;
    }

    const yearlyTotalWorkingDays = null;

    const updatedFiscalYear = {
      ...fiscalYear,
      yearlyTotalWorkingDays,
      totalWorkingDays: fiscalYear.totalWorkingDays.map((month) => ({
        ...month,
        monthlyWorkingDays: inputValues[month.id] || month.monthlyWorkingDays,
      })),
    };

    await modifyFiscalYear(updatedFiscalYear);
    setEditMode(null);
    setFiscalYear(initialState);
    setInputValues({});
  };

  const handleAddNewFiscalYear = async () => {
    const currentYear = new Date().getFullYear();
    const [startYear, endYear] = newFiscalYear.split('-').map(Number);

    if (
      startYear < 2000 ||
      endYear > currentYear + 1 ||
      endYear !== startYear + 1
    ) {
      setInputError(
        "Invalid fiscal year format. Please use 'YYYY-YYYY' format.",
      );
      return;
    }

    setInputError('');
    await createFiscalYear(newFiscalYear);
    setNewFiscalYear('');
  };

  const handleFiscalYearBlur = () => {
    const currentYear = new Date().getFullYear();
    const [startYear, endYear] = newFiscalYear.split('-').map(Number);

    if (startYear > currentYear || endYear > currentYear + 1) {
      setInputError('Future fiscal years are not allowed.');
    } else if (
      startYear < 2000 ||
      endYear > currentYear + 1 ||
      endYear !== startYear + 1
    ) {
      setInputError(
        "Invalid fiscal year format. Please use 'YYYY-YYYY' format.",
      );
    } else {
      setInputError('');
    }
  };

  if (loading) {
    return (
      <Container title={'Fiscal Year'}>
        <div className="mx-auto my-10 max-w-4xl">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="flex flex-col bg-white shadow-lg text-gray-800 border border-gray-300 w-full rounded-lg p-6 mb-6 animate-pulse"
            >
              <div className="flex justify-between items-center">
                <div>
                  <div className="w-40 h-6 bg-gray-300 rounded mb-2"></div>
                  <div className="w-24 h-4 bg-gray-300 rounded"></div>
                </div>
                <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
              </div>
            </div>
          ))}
        </div>
      </Container>
    ); // Show skeleton loading while fetching data
  }

  return (
    <Container title={'Fiscal Year'}>
      <div className="mx-auto my-10 max-w-4xl">
        <div className="m-2 flex justify-end my-5">
          <div>
            <input
              type="text"
              value={newFiscalYear}
              onChange={(e) => setNewFiscalYear(e.target.value)}
              onBlur={handleFiscalYearBlur}
              placeholder="Enter fiscal year (e.g., 2025-2026)"
              className="border rounded px-4 py-2 w-96 "
            />
            {inputError && (
              <div>
                <small className="text-red-600">{inputError}</small>
              </div>
            )}
            <div className="flex justify-end mt-2">
              <Button onClick={handleAddNewFiscalYear}>
                Add New Fiscal Year
              </Button>
            </div>
          </div>
        </div>
        {fiscalYearsData.map((fy) => (
          <div
            key={fy.id}
            className="flex flex-col bg-white shadow-lg text-gray-800 border border-gray-300 w-full rounded-lg p-6 mb-6"
          >
            <div className="flex justify-between items-center">
              <div>
                <div className="font-semibold text-xl border-b pb-2 mb-2">
                  {fy.fiscalYear}
                </div>
                <div className="text-sm text-gray-600">
                  Total Working Days:{' '}
                  <span className="font-medium">
                    {fy.yearlyTotalWorkingDays || '-'}
                  </span>
                </div>
              </div>
              <div className="flex items-center">
                <button
                  className="text-2xl text-blue-500 hover:text-blue-700"
                  onClick={() => toggleEditMode(fy.id)}
                >
                  {editMode === fy.id ? (
                    <IoCloseSharp className="text-red-600" />
                  ) : (
                    <FaEdit />
                  )}
                </button>
              </div>
            </div>

            {/* Month Details */}
            {editMode === fy.id && (
              <div className="mt-4">
                {fiscalYear?.totalWorkingDays?.map((month) => (
                  <div
                    key={month.id}
                    className="flex justify-between border-t py-3 text-gray-700"
                  >
                    <div className="font-medium">{month.month}</div>
                    <div className="text-gray-600">
                      <input
                        type="number"
                        name={`month-${month.id}`}
                        value={inputValues[month.id] ?? ''}
                        onChange={(e) =>
                          handleInputChange(month.id, e.target.value)
                        }
                        onBlur={(e) =>
                          handleBlur(month.id, e.target.value, month.month)
                        }
                        className="border rounded px-2 py-1"
                      />
                      {errors[month.id] && (
                        <div className="text-red-600 text-sm">
                          {errors[month.id]}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Save Button */}
                <button
                  className="mt-4 bg-blue-500 text-white px-4 py-2 rounded"
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Container>
  );
};

export default FiscalYear;
